var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// eslint-disable-next-line import/no-extraneous-dependencies
import { Combobox, Transition } from '@headlessui/react';
import clsx from 'clsx';
// eslint-disable-next-line import/no-extraneous-dependencies
import { useCallback, useEffect, useMemo, useState } from 'react';
import useDebounce from '../../../hooks/common/useDebounce';
import useInputErrorStyling from '../../../hooks/common/useInputErrorStyling';
import useTrackFocused from '../../../hooks/common/useTrackFocused';
import { getTestSelector } from '../../../utilities/tests';
import { Tooltip } from '../Tooltip';
import { TypeaheadClearButton, TypeaheadCustomOption, TypeaheadNoResultsPlaceholder, TypeaheadOption, TypeaheadRemoteSourcePlaceholder, } from './TypeaheadOptions';
export var SortDirection;
(function (SortDirection) {
    SortDirection[SortDirection["Ascending"] = 1] = "Ascending";
    SortDirection[SortDirection["Descending"] = 2] = "Descending";
})(SortDirection || (SortDirection = {}));
var defaultOptions = {
    noResultsLabel: 'No results match your query',
    remoteSourcePlaceholder: 'Keep typing to search for items',
    minCharactersForRemoteFetch: 2,
    clearable: true,
    useRegexMatchReplacement: true,
};
var Typeahead = function (_a) {
    var id = _a.id, data = _a.data, selected = _a.selected, onChange = _a.onChange, label = _a.label, className = _a.className, error = _a.error, _b = _a.placeholder, placeholder = _b === void 0 ? 'Start typing...' : _b, _c = _a.required, required = _c === void 0 ? false : _c, disabled = _a.disabled, _d = _a.testSelector, testSelector = _d === void 0 ? getTestSelector(label, 'typeahead') : _d, showLabel = _a.showLabel, tooltipOptions = _a.tooltipOptions, _e = _a.sortingOptions, sortingOptions = _e === void 0 ? {
        sortable: false,
    } : _e, _f = _a.options, options = _f === void 0 ? defaultOptions : _f;
    // Resolve any props with defaults
    var _g = __assign(__assign({}, defaultOptions), options), clearable = _g.clearable, noResultsLabel = _g.noResultsLabel, remoteSourcePlaceholder = _g.remoteSourcePlaceholder, minCharactersForRemoteFetch = _g.minCharactersForRemoteFetch, allowCustomInput = _g.allowCustomInput, useRegexMatchReplacement = _g.useRegexMatchReplacement;
    var sortedData = useSorting(data, sortingOptions);
    var _h = useState(''), query = _h[0], setQuery = _h[1];
    var handleChanged = function (selectedItem) {
        if ((selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.value) !== (selected === null || selected === void 0 ? void 0 : selected.value)) {
            onChange(selectedItem);
        }
    };
    var _j = useDataFiltering(query, sortedData, minCharactersForRemoteFetch), filteredData = _j.filteredData, isLoading = _j.isLoading, isResolved = _j.isResolved, isRemoteDataSource = _j.isRemoteDataSource;
    // See if what the user entered matches an item thats already in the list
    var potentialMatch = filteredData.find(function (x) { return x.label.toLowerCase() === query.toLowerCase(); });
    var _k = useTrackFocused({
        onBlur: function () {
            setTimeout(function () { return setQuery(''); }, 200);
            // Handle selecting an item automatically if their query is an exact item in the results list
            if (potentialMatch) {
                onChange(potentialMatch);
            }
        },
    }), isFocused = _k.isFocused, rest = __rest(_k, ["isFocused"]);
    var _l = useInputErrorStyling(error, disabled), invalid = _l.invalid, errorClassList = _l.classList, errorIcon = _l.icon;
    var overrideShow = isFocused && !selected;
    var _m = useState(false), disableMenu = _m[0], setDisableMenu = _m[1];
    return (_jsx(Combobox, { value: selected, onChange: handleChanged, disabled: disabled, children: function (_a) {
            var open = _a.open;
            return (_jsxs("div", { className: className, "data-cy": "".concat(testSelector, "-wrapper"), children: [_jsx(FormLabel, { required: required, showLabel: showLabel, tooltip: tooltipOptions, children: label }), _jsxs("div", { className: "relative", children: [_jsx(Combobox.Input, __assign({ autoComplete: "off", id: id, placeholder: placeholder, className: clsx.apply(void 0, __spreadArray(['block w-full px-3 py-2 mt-1 rounded-md shadow-sm sm:text-sm'], errorClassList, false)), onChange: function (event) {
                                    setQuery(event.target.value);
                                }, "aria-invalid": invalid, required: required, displayValue: function (item) { var _a; return (_a = item === null || item === void 0 ? void 0 : item.label) !== null && _a !== void 0 ? _a : ''; }, "data-cy": "".concat(testSelector, "-input") }, rest)), _jsxs("div", { className: "absolute top-px right-0 flex items-center h-[calc(100%-2px)] pr-1 mr-1 bg-white", children: [_jsx(TypeaheadClearButton, { clearable: clearable, selected: selected, setQuery: setQuery, onChange: onChange, testSelector: testSelector }), errorIcon] }), _jsx(Transition, { className: clsx('absolute z-10 w-full mt-1 overflow-auto transition-all border rounded-md shadow-md max-h-60 top-full', disableMenu && 'pointer-events-none'), enterFrom: "opacity-0 translate-y-2", enterTo: "opacity-100 translate-y-0", leaveFrom: "opacity-100 translate-y-0", leaveTo: "opacity-0 translate-y-2", show: open || overrideShow, onAnimationEnd: function () { return setDisableMenu(true); }, onAnimationStart: function () { return setDisableMenu(false); }, children: _jsxs(Combobox.Options, { static: true, className: "p-0 m-0", children: [_jsx(TypeaheadCustomOption, { query: query, testSelectorPrefix: testSelector, queryMatch: potentialMatch, allowCustomInput: allowCustomInput, isResolved: isResolved || !isRemoteDataSource }), filteredData.map(function (item) { return (_jsx(TypeaheadOption, { item: item, selected: selected, query: query, testSelectorPrefix: testSelector, useRegexMatchReplacement: useRegexMatchReplacement }, item.value)); }), _jsx(TypeaheadNoResultsPlaceholder, { isRemoteDataSource: isRemoteDataSource, isLoading: !!isLoading, isResolved: !!isResolved, filteredData: filteredData, query: query, minRemoteQueryCharacters: minCharactersForRemoteFetch, noResultsLabel: noResultsLabel }), _jsx(TypeaheadRemoteSourcePlaceholder, { isLoading: !!isLoading, isRemote: isRemoteDataSource, isResolved: !!isResolved, label: remoteSourcePlaceholder })] }) })] }), invalid && (_jsx("p", { className: "mt-2 text-sm text-red-600", id: "".concat(id, "-error"), children: error }))] }));
        } }));
};
export function useDataFiltering(query, data, minCharacters) {
    var _a;
    var _b = useState(false), isLoading = _b[0], setIsLoading = _b[1];
    // denotes that we received data back, regardless of if there were matches or not
    var _c = useState(false), isResolved = _c[0], setIsResolved = _c[1];
    var _d = useState([]), filteredData = _d[0], setFilteredData = _d[1];
    var cleanInput = ((_a = query.trim()) !== null && _a !== void 0 ? _a : '').toLocaleLowerCase();
    var isRemote = !Array.isArray(data);
    // eslint-disable-next-line @typescript-eslint/no-shadow
    var fetchDebounced = useDebounce(function (query) {
        if (query.length >= minCharacters) {
            setIsLoading(true);
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
            isRemote &&
                data(query)
                    .then(function (result) {
                    setIsLoading(false);
                    setIsResolved(true);
                    setFilteredData(result);
                })
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    .catch(function (error) { return setIsLoading(false); });
        }
    }, 500);
    var fetch = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-shadow
    function (query) {
        // We're just switching on whether or not we have data already, return result not needed
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        isRemote ? fetchDebounced(query) : setFilteredData(filter(cleanInput, data));
    }, [data, query]);
    useEffect(function () {
        // Handle resetting the search when we're making a new remote search
        if (isRemote) {
            setIsResolved(false);
            setFilteredData([]);
        }
        fetch(query);
    }, [data, query]);
    return {
        filteredData: filteredData,
        isRemoteDataSource: isRemote,
        resetData: function () { return setFilteredData([]); },
        isLoading: isLoading,
        isResolved: isResolved,
    };
}
function filter(query, data) {
    return data.filter(function (item) {
        var _a, _b;
        return item.label.toLocaleLowerCase().includes(query) ||
            ((_b = (_a = item.alias) === null || _a === void 0 ? void 0 : _a.toLocaleLowerCase()) === null || _b === void 0 ? void 0 : _b.includes(query));
    });
}
export function mapToTypeaheadItem(data, labelSelector, valueSelector) {
    var _a;
    return ((_a = data === null || data === void 0 ? void 0 : data.map(function (item) { return (__assign(__assign({}, item), { label: labelSelector(item), value: valueSelector(item) })); })) !== null && _a !== void 0 ? _a : []);
}
function useSorting(data, sortingOptions, deps) {
    if (deps === void 0) { deps = []; }
    var sortable = sortingOptions.sortable, _a = sortingOptions.sortProperty, sortProperty = _a === void 0 ? 'label' : _a, _b = sortingOptions.sortDirection, sortDirection = _b === void 0 ? SortDirection.Ascending : _b;
    return useMemo(function () {
        if (!Array.isArray(data))
            return data;
        // Handle cloning the data here so that we aren't modifying the original array
        var clonedData = data.slice();
        if (!sortable || !sortProperty || !data.length)
            return clonedData;
        var property = clonedData[0][sortProperty];
        if (!property)
            return clonedData;
        switch (typeof property) {
            case 'number':
                return clonedData.sort(function (first, second) {
                    var firstProp = first[sortProperty];
                    var secondProp = second[sortProperty];
                    return sortDirection === SortDirection.Ascending
                        ? firstProp - secondProp
                        : secondProp - firstProp;
                });
            case 'string':
                return clonedData.sort(function (first, second) {
                    var firstProp = first[sortProperty];
                    var secondProp = second[sortProperty];
                    return sortDirection === SortDirection.Ascending
                        ? firstProp.localeCompare(secondProp)
                        : secondProp.localeCompare(firstProp);
                });
            default:
                return data;
        }
    }, __spreadArray([data], deps, true));
}
var FormLabel = function (_a) {
    var children = _a.children, required = _a.required, tooltip = _a.tooltip, showLabel = _a.showLabel;
    return (_jsxs(Combobox.Label, { className: clsx('inline-flex items-center text-sm font-medium text-gray-700', showLabel && 'sr-only'), children: [children, required && _jsx("span", { className: "ml-1 text-red-500 not-sr-only", children: "*" }), tooltip && _jsx(Tooltip, __assign({}, tooltip, { wrapperClassName: "ml-1.5 inline-flex items-center h-full" }))] }));
};
export default Typeahead;
